import { type Ref } from 'react';
import { composeRenderProps, Button as RACButton, TooltipTrigger, type ButtonProps as RACButtonProps } from 'react-aria-components';
import { tv, type VariantProps } from 'tailwind-variants';
import { cn, focusRing } from '../utils';
import { interactiveIntentCompounds, statusVariants, type SemanticIntent } from '~/theme/variants';
import { ArrowUpDown, Eye, Loader, Pencil, Plus, RefreshCcw, Search, Trash2, type LucideIcon } from 'lucide-react';
import { Tooltip } from '../Tooltip';

//
const buttonStyles = tv({
	extend: statusVariants,
	base: 'inline-flex w-fit cursor-pointer items-center justify-center gap-2 rounded-lg text-center font-semibold transition-colors disabled:cursor-not-allowed',
	variants: {
		size: {
			xs: 'px-2 py-1 text-xs',
			sm: 'px-2.5 py-1.5 text-xs',
			md: 'h-[32px] px-4 text-xs',
			icon: 'p-2 text-sm',
			chip: 'px-2 py-0.5 text-xs',
			chipRounded: 'rounded-full px-3 py-1 text-xs'
		},
		appearance: {
			solid: 'shadow-sm',
			solidMuted: 'shadow-sm',
			outline: 'shadow-sm',
			ghost: 'shadow-none',
			subtle: 'shadow-none'
		},
		isInteractive: {
			true: '',
			false: ''
		}
	},
	compoundVariants: [
		...interactiveIntentCompounds,
		{ appearance: 'solid', isDisabled: true, class: 'shadow-none' },
		{ appearance: 'solidMuted', isDisabled: true, class: 'shadow-none' },
		{ appearance: 'outline', isDisabled: true, class: 'shadow-none' },
		{ intent: 'none', appearance: 'solid', class: 'focus:outline-none focus:ring-2 focus:ring-offset-1' }
	],
	defaultVariants: {
		isInteractive: true
	}
});

type ButtonVariants = VariantProps<typeof buttonStyles>;

interface ActionSpec {
	Icon: LucideIcon;
	ariaLabel: string;
	intent?: SemanticIntent;
}

const ACTIONS = {
	view: { Icon: Eye, ariaLabel: 'View' },
	add: { Icon: Plus, ariaLabel: 'Add' },
	edit: { Icon: Pencil, ariaLabel: 'Edit' },
	delete: { Icon: Trash2, ariaLabel: 'Delete', intent: 'destructive' },
	reorder: { Icon: ArrowUpDown, ariaLabel: 'Reorder' },
	refresh: { Icon: RefreshCcw, ariaLabel: 'Refresh' },
	search: { Icon: Search, ariaLabel: 'Search' }
} satisfies Record<string, ActionSpec>;

type ButtonAction = keyof typeof ACTIONS;

const STATUS_INDICATOR_COLORS = {
	success: 'bg-green-600',
	warning: 'bg-yellow-500',
	destructive: 'bg-red-600'
} satisfies Record<string, string>;

type StatusIndicator = keyof typeof STATUS_INDICATOR_COLORS;

function getAction(actionType: ButtonAction | undefined): ActionSpec | undefined {
	return actionType ? ACTIONS[actionType] : undefined;
}

export interface ButtonBaseProps extends RACButtonProps {
	actionType?: ButtonAction;
	intent?: ButtonVariants['intent'];
	appearance?: ButtonVariants['appearance'];
	size?: ButtonVariants['size'];
	ref?: Ref<HTMLButtonElement>;
	label?: string;
	Icon?: LucideIcon;
	iconClassName?: string;
	useTooltip?: boolean;
	statusIndicator?: StatusIndicator;
}

function resolveVariants({ size, intent, appearance, actionType, label }: ButtonBaseProps) {
	const resolvedSize = size ?? (label ? 'md' : 'icon');
	const iconOnly = resolvedSize === 'icon';
	return {
		size: resolvedSize,
		intent: intent ?? getAction(actionType)?.intent ?? (iconOnly ? 'neutral' : 'primary'),
		appearance: appearance ?? (iconOnly ? 'outline' : 'solid')
	};
}

export function ButtonBase({
	actionType,
	intent,
	appearance,
	size,
	label,
	Icon,
	iconClassName,
	isPending,
	useTooltip,
	statusIndicator,
	children,
	...props
}: ButtonBaseProps) {
	if (!actionType && !label && !Icon && !children) {
		throw new Error('Button must have at least one of the following: actionType, label, or Icon');
	} else if ((actionType || label || Icon) && children) {
		throw new Error('Button cannot have both children and any of the following: actionType, label, or Icon');
	}

	const action = getAction(actionType);
	const ButtonIcon = Icon ?? action?.Icon;
	const variants = resolveVariants({ size, intent, appearance, actionType, label });

	const tooltipLabel = label ?? props['aria-label'] ?? action?.ariaLabel;
	if (useTooltip && !tooltipLabel) {
		throw new Error('Button with useTooltip must have a label or aria-label');
	}

	const button = (
		<RACButton
			type="button"
			{...props}
			isPending={isPending}
			aria-label={props['aria-label'] ?? (!label ? action?.ariaLabel : undefined)}
			className={composeRenderProps(props.className, (className, renderProps) =>
				cn(
					focusRing({ ...renderProps, intent: variants.intent }),
					buttonStyles({ ...variants, isDisabled: renderProps.isDisabled }),
					statusIndicator && 'relative',
					className
				)
			)}
		>
			{composeRenderProps(children, resolvedChildren => (
				<>
					{resolvedChildren ? (
						resolvedChildren
					) : (
						<>
							{ButtonIcon ? (
								isPending ? (
									<Loader className={cn('size-4 animate-spin', iconClassName)} />
								) : (
									<ButtonIcon className={cn('size-4', iconClassName)} />
								)
							) : null}
							{label}
						</>
					)}
					{statusIndicator && (
						<span aria-hidden className={cn('absolute -right-1 -top-1 size-2.5 rounded-full', STATUS_INDICATOR_COLORS[statusIndicator])} />
					)}
				</>
			))}
		</RACButton>
	);

	if (!useTooltip) {
		return button;
	}

	return (
		<TooltipTrigger>
			{button}
			<Tooltip>{tooltipLabel}</Tooltip>
		</TooltipTrigger>
	);
}
